import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import { ResizingArrow, SortingArrowDown, SortingArrowUp } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { NUMBER } from '../../constant/Number.constant';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import { RoutePath } from '../../routes';
import {
  ComputedPromotionStatus,
  Promotion,
  PromotionDiscountType,
  PromotionResourceType,
} from '../../stores/types/promotions.interface';
import { ColorType, Icon, Label, LabelType } from '../_ui';
import { captureSortColumnEvent, DataTableName, DataTablePage } from '../_ui/data-table/utils';
import { GridPager } from '../_ui/grid/Grid-Pager.component';
import NoPromotion from './NoPromotion.component';
import PromotionGridMenu from './PromotionGridMenu.component';
import PromotionStatus from './PromotionStatus.component';
import { buildAddPromoCodePath, formatPromotionWindow } from './promotion.utils';

const ROW_PER_PAGE = NUMBER.TWENTY;
const PROMOTION_TABLE_SORT_CONFIG_STORAGE_KEY = 'promotionTable.sortConfig';

const formatDiscount = (
  discountType: PromotionDiscountType,
  discountValue: number,
  t: TFunction,
): string =>
  discountType === PromotionDiscountType.PERCENTAGE
    ? `${discountValue}% ${t('off')}`
    : `$${discountValue} ${t('off')}`;

const formatAppliedAt = (
  applicableResources: Partial<Record<PromotionResourceType, string[]>>,
  t: TFunction,
): string => {
  const locationCount = applicableResources[PromotionResourceType.LOCATION]?.length ?? 0;
  const chargerCount = applicableResources[PromotionResourceType.CHARGER]?.length ?? 0;

  const locationLabel = locationCount === 1 ? t('locationLower') : t('locationsLower');
  const chargerLabel = chargerCount === 1 ? t('chargerLower') : t('chargersLower');

  return `${locationCount} ${locationLabel}, ${chargerCount} ${chargerLabel}`;
};

const formatCodes = (promoCodeCount: number, t: TFunction): string =>
  promoCodeCount === 1 ? `${promoCodeCount} ${t('code')}` : `${promoCodeCount} ${t('codes')}`;

type Props = {
  data: Promotion[];
  loading: boolean;
};

const PromotionTable = ({ data, loading: isPromotionLoading }: Props) => {
  const { t, i18n } = useTranslation();

  const [pageIndex, setPageIndex] = useState(1);

  const promotionsData = useMemo(() => data ?? [], [data]);

  const [sorting, setSorting] = useLocalStorageState<SortingState>(
    PROMOTION_TABLE_SORT_CONFIG_STORAGE_KEY,
    [],
  );

  const { navigateWithCompanyId, navigate } = useNavigateWithSearchParam();

  const navigateToAddPromoCode = useCallback(
    (promotion: Promotion) =>
      navigate(buildAddPromoCodePath(promotion.promotionId, promotion.companyId)),
    [navigate],
  );

  const renderTextCell = ({
    text,
    color = ColorType.BLACK,
    className = 'whitespace-nowrap truncate',
  }: {
    text: string;
    color?: ColorType;
    className?: string;
  }) => (
    <Label
      isLoading={isPromotionLoading}
      className={className}
      text={text}
      type={LabelType.BODY3}
      color={color}
    />
  );

  // columns
  const columns: ColumnDef<Promotion>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: t('promotion_name'),
        enableResizing: true,
        cell: ({ row }) => renderTextCell({ text: row.original.name }),
        size: 150,
        minSize: 150,
        enableSorting: true,
      },
      {
        accessorKey: 'status',
        header: t('status'),
        enableResizing: true,
        cell: ({ row }) => {
          return <PromotionStatus isLoading={isPromotionLoading} status={row.original.status} />;
        },
        size: 120,
        minSize: 120,
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        id: 'discountValue',
        header: t('discount_value'),
        accessorFn: (row) => formatDiscount(row.discountType, row.discountValue, t),
        cell: ({ row }) =>
          renderTextCell({
            text: formatDiscount(row.original.discountType, row.original.discountValue, t),
          }),
        size: 120,
        minSize: 120,
        enableSorting: true,
      },
      {
        id: 'appliedAt',
        header: t('discount_applied_at'),
        accessorFn: (row) => formatAppliedAt(row.applicableResources, t),
        cell: ({ row }) => {
          return (
            <div
              className='whitespace-nowrap truncate cursor-pointer'
              onClick={() =>
                navigateWithCompanyId(
                  `../${RoutePath.PROMOTION_VIEW.replace(
                    ':promotionId',
                    row.original.promotionId,
                  )}`,
                  row.original.companyId,
                )
              }
            >
              {renderTextCell({
                text: formatAppliedAt(row.original.applicableResources, t),
                color: ColorType.DENIM,
              })}
            </div>
          );
        },
        size: 150,
        minSize: 150,
        enableSorting: true,
      },
      {
        id: 'promotionWindow',
        header: t('promotion_window'),
        accessorFn: (row) =>
          formatPromotionWindow(row.validFrom, row.validUntil, {
            locale: i18n.language,
            endFallback: t('no_end_date'),
          }),
        cell: ({ row }) =>
          renderTextCell({
            text: formatPromotionWindow(row.original.validFrom, row.original.validUntil, {
              locale: i18n.language,
              endFallback: t('no_end_date'),
            }),
          }),
        size: 160,
        minSize: 160,
        enableSorting: true,
      },
      {
        id: 'codes',
        header: t('codes'),
        accessorFn: (row) => formatCodes(row.promoCodeCount, t),
        cell: ({ row }) => {
          const count = row.original.promoCodeCount;
          if (count) {
            return renderTextCell({ text: formatCodes(count, t) });
          }
          // Archived promotions can't accept new codes, so show a static count instead
          // of the "Add code(s)" link.
          if (row.original.status === ComputedPromotionStatus.DELETED) {
            return renderTextCell({ text: formatCodes(0, t) });
          }
          return (
            <Label
              text={t('add_code_codes')}
              type={LabelType.BODY3}
              color={ColorType.BRAND3}
              className='cursor-pointer whitespace-nowrap'
              onClick={() => navigateToAddPromoCode(row.original)}
            />
          );
        },
        size: 100,
        minSize: 100,
        enableSorting: true,
      },
      {
        accessorKey: 'action',
        header: '',
        enableResizing: false,
        cell: ({ row }) => {
          return (
            <div className='flex justify-end items-start'>
              <PromotionGridMenu promotion={row.original} />
            </div>
          );
        },
        size: 80,
        minSize: 80,
        enableSorting: false,
      },
    ],
    [t, i18n.language, isPromotionLoading, navigateWithCompanyId, navigateToAddPromoCode],
  );

  const promotionTable = useReactTable({
    data: promotionsData,
    columns: columns,
    state: {
      sorting,
      pagination: {
        pageIndex: pageIndex - 1,
        pageSize: ROW_PER_PAGE,
      },
    },
    onSortingChange: (updaterOrValue) => {
      // Get the new sorting state
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

      // Capture the sort column event
      if (newSorting.length > 0 && newSorting[0]) {
        captureSortColumnEvent(
          DataTablePage.PROMOTIONS,
          DataTableName.PROMOTIONS,
          newSorting[0].id,
        );
      }

      // Update the sorting state
      setSorting(newSorting);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    manualPagination: false,
  });

  const promotionsDataCount = promotionTable.getFilteredRowModel().rows.length;

  const totalPages = Math.ceil(promotionsDataCount / ROW_PER_PAGE);

  // overview top-level return
  return (
    <div className='overflow-hidden'>
      {promotionsDataCount || isPromotionLoading ? (
        <div className='overflow-visible mt-5'>
          <div className='overflow-x-auto overflow-y-visible data-table-scroll'>
            <table className='data-table'>
              <thead>
                {promotionTable.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className='bg-white'>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        data-column-id={header.column.id}
                        style={{
                          width: header.getSize(),
                          minWidth: header.column.columnDef.minSize ?? 100,
                          position: 'relative',
                        }}
                        className='px-4 py-3 text-left text-sm font-semibold font-inter text-grey6'
                      >
                        <div
                          className={header.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                          onClick={
                            header.column.getCanSort()
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {/* Sort arrows */}
                          {header.column.getCanSort() && (
                            <button
                              type='button'
                              className='absolute right-0 top-1/2 -translate-y-1/2 flex flex-col pr-2'
                            >
                              <Icon
                                src={SortingArrowUp}
                                size={IconSize.SIZE_12x12}
                                color={
                                  header.column.getIsSorted() === 'asc'
                                    ? ColorType.GREY6
                                    : ColorType.GREY3
                                }
                              />
                              <Icon
                                src={SortingArrowDown}
                                size={IconSize.SIZE_12x12}
                                color={
                                  header.column.getIsSorted() === 'desc'
                                    ? ColorType.GREY6
                                    : ColorType.GREY3
                                }
                                className='-mt-1'
                              />
                            </button>
                          )}
                        </div>

                        {/* Resize Handle */}
                        {header.column.getCanResize() && (
                          <div
                            onDoubleClick={() => header.column.resetSize()}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className='resizer'
                            style={{
                              cursor: `url(${ResizingArrow}) 8 8, col-resize`,
                            }}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {promotionTable.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                        className='px-4 py-3 text-sm text-gray-900 overflow-hidden whitespace-nowrap'
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <GridPager
              totalPage={totalPages}
              currentPage={pageIndex}
              loadPage={setPageIndex}
              showPageSearch
            />
          )}
        </div>
      ) : (
        <NoPromotion isLoading={isPromotionLoading} />
      )}
    </div>
  );
};

export default PromotionTable;
